<template>
  <div data-app>
    <v-card>
      <v-card-title>
        <v-row>
          <v-col cols="12" sm="6" md="6" style="color: #e33354" class="ml-4">
            <h3>{{ cardTitle }}</h3>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" sm="3" md="3">
            <v-text-field
              v-model="search"
              append-icon="search"
              :label="$t('FORMS.search')"
              single-line
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-data-table
          v-model="selectedItems"
          :headers="headers"
          :items="colleagueCollection"
          :search="search"
          :sort-by="['id']"
          sort-desc="true"
          :loading="loadingTable"
          show-select
          item-key="id"
          @click:row="handleClickItem"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <ColleagueModalForm
                :modalData="modalData"
                :permissions="permissions"
                :statuses="statuses"
                :endPoint="endPoint"
                @closeModalForm="handleCloseModalForm"
                @saveModalForm="handleSaveModalForm"
                @new="handleNew"
              ></ColleagueModalForm>
              <template v-if="selectedItems.length > 0">
                <v-divider vertical class="mx-4"></v-divider>
                <span class="grey--text text--darken-1 mr-3">
                  {{ selectedItems.length }}
                </span>
                <v-btn
                  v-if="permissionCan('update')"
                  small
                  color="success"
                  class="mr-2"
                  :loading="bulkLoading"
                  @click="handleBulkStatus(1)"
                >
                  {{ $t("FORMS.active") }}
                </v-btn>
                <v-btn
                  v-if="permissionCan('update')"
                  small
                  color="error"
                  :loading="bulkLoading"
                  @click="handleBulkStatus(0)"
                >
                  {{ $t("FORMS.inactive") }}
                </v-btn>
              </template>
              <v-spacer></v-spacer>
              <DeleteModalDialog
                :dialogDelete="dialogDelete"
                @closeDelete="handleCloseDelete"
                @deleteItemConfirm="handleDeleteItemConfirm"
              >
              </DeleteModalDialog>
            </v-toolbar>
          </template>
          <template v-slot:item.translated="{ item }">
            <v-icon v-if="item.translated" color="green" small
              >check_circle</v-icon
            >
          </template>

          <template v-slot:item.categories="{ item }">
            <span
              v-for="(cat, i) in (categoriesByColleagueId[item.id] || [])"
              :key="cat.id"
            >{{ cat.name }}<span v-if="i < (categoriesByColleagueId[item.id] || []).length - 1">, </span></span>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip
              x-small
              :color="item.status == 1 ? 'success' : 'error'"
              dark
            >
              {{ item.status == 1 ? $t("FORMS.active") : $t("FORMS.inactive") }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-icon
              color="primary"
              v-if="permissionCan('delete')"
              small
              @click="deleteItem(item)"
            >
              mdi-delete
            </v-icon>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import SiteService from "@/core/services/site.service.js";

import { listModelsMixins } from "@/view/mixins/listModelsMixins.js";

import ColleagueModalForm, { initialFormData } from "./ColleagueModalForm";

import DeleteModalDialog from "@/view/components/DeleteModalDialog";

export const PERMISSION_TO = "colleagues.colleague.";

export default {
  name: "Colleagues",
  components: { ColleagueModalForm, DeleteModalDialog },
  mixins: [listModelsMixins],
  data() {
    return {
      cardTitle: this.$t("MENU.COLLEAGUES"),
      search: "",
      routePath: "/cms/colleagues/colleagues/",
      permissionTo: PERMISSION_TO,
      permissions: JSON.parse(localStorage.permissions),
      headers: [
        {
          text: this.$t("TABLE.translated"),
          value: "translated",
          width: "100px",
        },
        {
          text: this.$t("TABLE.name"),
          value: "name",
        },
        {
          text: this.$t("MENU.COLLEAGUE_CATEGORIES"),
          value: "categories",
          sortable: false,
        },
        {
          text: this.$t("TABLE.status"),
          value: "status",
          width: "120px",
        },
        {
          text: this.$t("TABLE.actions"),
          value: "actions",
          sortable: false,
          width: "100px",
        },
      ],

      modalData: {
        dialog: false,
        editedId: null,
      },
      editedItem: Object.assign({}, initialFormData()),

      dialogDelete: false,
      selectedItems: [],
      bulkLoading: false,
    };
  },

  computed: {
    ...mapGetters(["colleagueCollection", "statuses", "colleagueCategoryCollection"]),

    endPoint() {
      return (
        "colleagues/admin/" + SiteService.getActiveSiteId() + "/colleague/"
      );
    },

    categoriesByColleagueId() {
      const map = {};
      for (const cat of this.colleagueCategoryCollection) {
        const colleagues = cat.colleagues || [];
        for (const colId of colleagues) {
          if (!map[colId]) map[colId] = [];
          map[colId].push(cat);
        }
      }
      return map;
    },
  },

  watch: {
    dialogDelete(val) {
      val || this.handleCloseDelete();
    },
    "$route.params.id": {
      handler: function (id) {
        if (id) {
          this.editItem(id);
        }
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    ...mapActions(["fetchColleague", "bulkStatusColleague", "fetchColleagueCategory"]),
    fetchModel() {
      return this.fetchColleague();
    },
    handleBulkStatus(status) {
      this.bulkLoading = true;
      this.bulkStatusColleague({
        ids: this.selectedItems.map((item) => item.id),
        status,
      })
        .then(() => {
          this.selectedItems = [];
          this.fetchModel();
        })
        .catch((error) => {
          console.log("Error!: ", error);
        })
        .finally(() => {
          this.bulkLoading = false;
        });
    },
  },

  mounted() {
    this.loadingTable = true;
    this.fetchColleagueCategory();
    this.fetchModel().then(() => (this.loadingTable = false));
  },
};
</script>
